import { useState } from 'react'
import { MapPin, User, FileText, Loader2 } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/sheet'
import { cn } from '@/shared/utils'
import { useOrderDetail, useUpdateOrderStatus } from '../hooks/useOrders'

const STATUS_COLORS: Record<string, string> = {
  CREATED: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-orange-100 text-orange-800',
  PAID: 'bg-blue-100 text-blue-800',
  OUT_FOR_DELIVERY: 'bg-purple-100 text-purple-800',
  SHIPPED: 'bg-purple-100 text-purple-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800',
  RETURNED: 'bg-gray-100 text-gray-800',
}

const STATUS_LABELS: Record<string, string> = {
  CREATED: 'Created',
  CONFIRMED: 'Confirmed',
  PAID: 'Confirmed',
  PREPARING: 'Preparing',
  SHIPPED: 'Shipped',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
  RETURN_REQUESTED: 'Return Requested',
  RETURNED: 'Returned',
}

const FOOD_NEXT_STATUS: Record<string, { next: string; label: string } | null> = {
  CREATED: { next: 'CONFIRMED', label: 'Accept Order' },
  CONFIRMED: { next: 'PREPARING', label: 'Start Preparing' },
  // OUT_FOR_DELIVERY and DELIVERED are handled by the delivery rider
}

const CLOTHING_NEXT_STATUS: Record<string, { next: string; label: string } | null> = {
  CREATED: { next: 'PAID', label: 'Accept Order' },
  // SHIPPED and DELIVERED are handled by the delivery rider
}

const CANCELLABLE = ['CREATED', 'CONFIRMED', 'PAID']

interface OrderDetailProps {
  vendorId: string
  orderType: string
  orderId: string
  open: boolean
  onClose: () => void
}

export function OrderDetail({ vendorId, orderType, orderId, open, onClose }: OrderDetailProps) {
  const { data: order, isLoading } = useOrderDetail(vendorId, orderType, orderId)
  const { mutate: updateStatus, isPending } = useUpdateOrderStatus(vendorId)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  const nextStatusMap = orderType === 'FOOD' ? FOOD_NEXT_STATUS : CLOTHING_NEXT_STATUS
  const nextAction = order ? nextStatusMap[order.status] : null
  const canCancel = order ? CANCELLABLE.includes(order.status) : false

  const handleStatusUpdate = (status: string) => {
    updateStatus({ type: orderType, orderId, status }, { onSuccess: onClose })
  }

  const handleCancel = () => {
    updateStatus(
      { type: orderType, orderId, status: 'CANCELLED', reason: cancelReason || undefined },
      { onSuccess: () => { setCancelDialogOpen(false); setCancelReason(''); onClose() } }
    )
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Order Details</SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : order ? (
          <div className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold">#{order.orderNumber || order.id.slice(0, 8)}</p>
                <p className="text-sm text-muted-foreground">{new Date(order.createdAt).toLocaleString()}</p>
              </div>
              <Badge className={cn('text-sm', STATUS_COLORS[order.status] || '')}>
                {STATUS_LABELS[order.status] || order.status.replace(/_/g, ' ')}
              </Badge>
            </div>

            {order.customer?.account && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <h3 className="font-medium text-sm text-muted-foreground">Customer</h3>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{order.customer.account.name}</span>
                  </div>
                </div>
              </div>
            )}

            {order.deliveryLocation && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-medium text-sm text-muted-foreground mb-2">Delivery Address</h3>
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <span>{order.deliveryLocation.address}</span>
                </div>
              </div>
            )}

            <div>
              <h3 className="font-medium text-sm text-muted-foreground mb-3">Items</h3>
              <div className="divide-y rounded-lg border">
                {order.items?.map((item: { id?: string; name?: string; productName?: string; size?: string; color?: string; quantity: number; price: number }, idx: number) => (
                  <div key={item.id || idx} className="flex items-center justify-between p-3">
                    <div>
                      <p className="font-medium text-sm">{item.name || item.productName}</p>
                      {(item.size || item.color) && (
                        <p className="text-xs text-muted-foreground">
                          {[item.size, item.color].filter(Boolean).join(' / ')}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm">x{item.quantity}</p>
                      <p className="text-sm font-medium">₹{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3 pt-3 border-t font-semibold">
                <span>Order Value</span>
                <span>₹{(order.subtotal ?? order.totalAmount).toFixed(2)}</span>
              </div>
            </div>

            {/* Settlement Breakdown */}
            {order.vendorNetPayout != null && (
              <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
                <h3 className="font-medium text-sm text-emerald-800">Your Earnings</h3>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Order Value</span>
                    <span>₹{(order.subtotal ?? order.totalAmount).toFixed(2)}</span>
                  </div>
                  {order.vendorCommission != null && (
                    <div className="flex justify-between text-red-600">
                      <span>Commission (17%)</span>
                      <span>-₹{order.vendorCommission.toFixed(2)}</span>
                    </div>
                  )}
                  {order.vendorCommissionGst != null && (
                    <div className="flex justify-between text-red-600">
                      <span>GST on Commission (18%)</span>
                      <span>-₹{order.vendorCommissionGst.toFixed(2)}</span>
                    </div>
                  )}
                  {order.vendorTcs != null && (
                    <div className="flex justify-between text-red-600">
                      <span>TCS (1%)</span>
                      <span>-₹{order.vendorTcs.toFixed(2)}</span>
                    </div>
                  )}
                  {order.vendorTds != null && (
                    <div className="flex justify-between text-red-600">
                      <span>TDS (1%)</span>
                      <span>-₹{order.vendorTds.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-1.5 border-t border-emerald-200 text-emerald-800">
                    <span>Net Payout</span>
                    <span>₹{order.vendorNetPayout.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {order.notes && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-medium text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                  <FileText className="w-4 h-4" /> Notes
                </h3>
                <p className="text-sm">{order.notes}</p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              {nextAction && (
                <Button className="flex-1" onClick={() => handleStatusUpdate(nextAction.next)} disabled={isPending}>
                  {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {nextAction.label}
                </Button>
              )}
              {canCancel && (
                <>
                  <Button variant="destructive" onClick={() => setCancelDialogOpen(true)} disabled={isPending}>
                    Cancel
                  </Button>
                  <AlertDialog open={cancelDialogOpen} onOpenChange={(v) => { if (!v) { setCancelDialogOpen(false); setCancelReason('') } }}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel this order?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to cancel order #{order?.orderNumber || orderId.slice(0, 8)}? This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div>
                        <textarea
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                          placeholder="Reason for cancellation *"
                          className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                          rows={3}
                        />
                        {cancelReason.trim().length === 0 && (
                          <p className="text-xs text-destructive mt-1">Cancellation reason is required</p>
                        )}
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep Order</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50" disabled={isPending || cancelReason.trim().length === 0}>
                          {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Yes, Cancel Order
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">Order not found.</p>
        )}
      </SheetContent>
    </Sheet>
  )
}
